import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import QuoteModal from '../../components/feature/QuoteModal';
import SchemaOrgHead from '../../components/feature/SchemaOrgHead';

const teamMembers = [
  {
    name: 'Rosa Martínez',
    roleKey: 'abp_tr1',
    bioKey: 'abp_tb1',
    avatar: 'https://public.readdy.ai/ai/img_res/c66c7033afe0d52c6b089b1337fa2cfd.jpg',
  },
  {
    name: 'Carlos Rivera',
    roleKey: 'abp_tr2',
    bioKey: 'abp_tb2',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20latin%20man%20supervisor%20cleaning%20operations%20team%20leader%20uniform%20polo%20shirt%20smiling%20confident%20portrait%20neutral%20background%2038%20years%20old&width=300&height=380&seq=team002&orientation=portrait',
  },
  {
    name: 'Lucía Pérez',
    roleKey: 'abp_tr3',
    bioKey: 'abp_tb3',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20latina%20woman%20cleaning%20specialist%20smiling%20friendly%20portrait%20bright%20background%20construction%20cleaning%20expert%20uniform%2034%20years%20old&width=300&height=380&seq=team003&orientation=portrait',
  },
];

const milestones = [
  { year: '2025', tKey: 'abp_m1_t', dKey: 'abp_m1_d' },
  { year: 'HOY', tKey: 'abp_m2_t', dKey: 'abp_m2_d' },
  { year: 'PRONTO', tKey: 'abp_m3_t', dKey: 'abp_m3_d' },
  { year: '2026', tKey: 'abp_m4_t', dKey: 'abp_m4_d' },
];

const values = [
  { icon: 'ri-shield-check-line', color: 'from-brand-green to-brand-green-dark', tKey: 'abp_v1_t', dKey: 'abp_v1_d' },
  { icon: 'ri-star-line', color: 'from-brand-blue to-brand-blue-dark', tKey: 'abp_v2_t', dKey: 'abp_v2_d' },
  { icon: 'ri-group-line', color: 'from-brand-green-dark to-brand-blue', tKey: 'abp_v3_t', dKey: 'abp_v3_d' },
  { icon: 'ri-recycle-line', color: 'from-brand-green to-brand-blue', tKey: 'abp_v4_t', dKey: 'abp_v4_d' },
];

const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

const nosotrosSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  '@id': `${siteUrl}/nosotros`,
  url: `${siteUrl}/nosotros`,
  name: 'Sobre Nosotros | Limpieza Pro The Villages FL',
  description:
    'Conoce al equipo de Limpieza Pro The Villages FL: nuestra misión, visión, valores y la historia de cómo nos convertimos en líderes en limpieza residencial en The Villages, FL.',
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Inicio', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Nosotros', item: `${siteUrl}/nosotros` },
    ],
  },
  mainEntity: {
    '@type': 'Organization',
    name: 'Limpieza Pro The Villages FL',
    foundingDate: '2019',
    numberOfEmployees: { '@type': 'QuantitativeValue', value: 10 },
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'The Villages',
      addressRegion: 'FL',
      addressCountry: 'US',
    },
  },
};

export default function NosotrosPage() {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <>
      <SchemaOrgHead
        schema={nosotrosSchema}
        pageTitle="Sobre Nosotros | Limpieza Residencial en The Villages, FL — Limpieza Pro"
        pageDescription="Conoce al equipo de Limpieza Pro The Villages FL. Desde 2019 ofrecemos limpieza residencial post-mudanza, ventanas, lavado a presión y valet trash en The Villages, FL con más de 500 clientes satisfechos."
        canonicalPath="/nosotros"
      />
      {/* Hero */}
      <section className="relative pt-36 pb-20 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=professional%20cleaning%20company%20team%20in%20uniform%20green%20shirts%20smiling%20group%20photo%20residential%20home%20exterior%20sunny%20day%20teamwork%20professional%20miami%20florida&width=1920&height=600&seq=abouthero001&orientation=landscape"
          alt="Nuestro equipo profesional"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-blue-dark/85 to-brand-green-dark/70" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <span className="inline-block bg-white/10 border border-white/20 text-brand-green-light text-xs font-semibold px-4 py-1.5 rounded-full mb-5 tracking-wider uppercase">
            {t('abp_badge')}
          </span>
          <h1 className="font-heading font-bold text-white text-5xl mb-5">{t('abp_title')}</h1>
          <p className="text-white/75 text-base max-w-xl mx-auto leading-relaxed">
            {t('abp_desc')}
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-brand-green font-semibold text-sm uppercase tracking-wider">{t('abp_ess')}</span>
              <h2 className="font-heading font-bold text-brand-blue-dark text-4xl mt-2 mb-6 leading-tight">
                {t('abp_h2')} <span className="text-brand-green">{t('abp_h2_accent')}</span> {t('abp_h2_end')}
              </h2>
              <p className="text-gray-500 text-sm leading-relaxed mb-5">{t('abp_p1')}</p>
              <p className="text-gray-500 text-sm leading-relaxed mb-8">{t('abp_p2')}</p>

              {/* Mission / Vision */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-brand-green-light/30 rounded-2xl p-5">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-brand-green mb-3">
                    <i className="ri-focus-line text-white text-lg" />
                  </div>
                  <h3 className="font-semibold text-brand-blue-dark text-sm mb-1">{t('abp_mission_t')}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{t('abp_mission_d')}</p>
                </div>
                <div className="bg-brand-blue-light/30 rounded-2xl p-5">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-brand-blue mb-3">
                    <i className="ri-eye-line text-white text-lg" />
                  </div>
                  <h3 className="font-semibold text-brand-blue-dark text-sm mb-1">{t('abp_vision_t')}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{t('abp_vision_d')}</p>
                </div>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20residential%20cleaning%20team%20working%20together%20modern%20home%20interior%20clean%20bright%20natural%20light%20professional%20cleaning%20service%20quality%20work%20miami%20florida%20friendly%20team&width=700&height=520&seq=about003&orientation=landscape"
                alt="Equipo trabajando profesionalmente"
                className="w-full rounded-2xl object-cover object-top h-96"
              />
              <div className="absolute -bottom-6 -right-4 bg-white rounded-2xl p-5 flex gap-4">
                <div className="text-center">
                  <p className="font-heading font-bold text-brand-green-dark text-3xl">100%</p>
                  <p className="text-gray-400 text-xs">{t('abp_stat1')}</p>
                </div>
                <div className="w-px bg-gray-100" />
                <div className="text-center">
                  <p className="font-heading font-bold text-brand-blue text-3xl">Gratis</p>
                  <p className="text-gray-400 text-xs">{t('abp_stat2')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="text-brand-green font-semibold text-sm uppercase tracking-wider">{t('abp_timeline_tag')}</span>
            <h2 className="font-heading font-bold text-brand-blue-dark text-3xl mt-2 mb-4">{t('abp_timeline_h')}</h2>
            <div className="w-12 h-1 bg-brand-green rounded-full mx-auto" />
          </div>
          <div className="relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gray-200 -translate-x-1/2" />
            <div className="space-y-10">
              {milestones.map((m, idx) => (
                <div key={m.year} className={`flex items-center gap-8 ${idx % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                  <div className={`flex-1 text-${idx % 2 === 0 ? 'right' : 'left'}`}>
                    <div className={`inline-block bg-white rounded-2xl p-5 max-w-xs ${idx % 2 === 0 ? 'ml-auto' : 'mr-auto'}`}>
                      <p className="font-semibold text-brand-blue-dark text-sm mb-1">{t(m.tKey)}</p>
                      <p className="text-gray-500 text-xs leading-relaxed">{t(m.dKey)}</p>
                    </div>
                  </div>
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-gradient-to-br from-brand-green to-brand-blue text-white font-heading font-bold text-sm flex-shrink-0 z-10">
                    {m.year}
                  </div>
                  <div className="flex-1" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="text-brand-green font-semibold text-sm uppercase tracking-wider">{t('abp_team_tag')}</span>
            <h2 className="font-heading font-bold text-brand-blue-dark text-3xl mt-2 mb-4">{t('abp_team_h')}</h2>
            <div className="w-12 h-1 bg-brand-green rounded-full mx-auto" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {teamMembers.map((member) => (
              <div key={member.name} className="text-center group">
                <div className="relative overflow-hidden rounded-2xl mb-5 h-72">
                  <img
                    src={member.avatar}
                    alt={member.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-blue-dark/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                <h3 className="font-heading font-bold text-brand-blue-dark text-lg">{member.name}</h3>
                <p className="text-brand-green text-sm font-semibold mb-2">{t(member.roleKey)}</p>
                <p className="text-gray-500 text-xs leading-relaxed max-w-xs mx-auto">{t(member.bioKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-brand-blue-dark text-3xl mb-3">{t('abp_val_h')}</h2>
            <div className="w-12 h-1 bg-brand-green rounded-full mx-auto" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {values.map((v) => (
              <div key={v.tKey} className="bg-white rounded-2xl p-6 text-center">
                <div className={`w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br ${v.color} mx-auto mb-4`}>
                  <i className={`${v.icon} text-white text-2xl`} />
                </div>
                <h3 className="font-semibold text-brand-blue-dark text-sm mb-2">{t(v.tKey)}</h3>
                <p className="text-gray-400 text-xs leading-relaxed">{t(v.dKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-gradient-to-r from-brand-blue-dark to-brand-green-dark">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading font-bold text-white text-3xl mb-4">{t('abp_cta_t')}</h2>
          <p className="text-white/75 text-sm mb-8">{t('abp_cta_d')}</p>
          <button
            onClick={() => setQuoteOpen(true)}
            className="bg-brand-green hover:bg-white hover:text-brand-green-dark text-white font-bold px-10 py-4 rounded-full transition-all cursor-pointer whitespace-nowrap"
          >
            {t('abp_cta_btn')}
          </button>
        </div>
      </section>

      <QuoteModal isOpen={quoteOpen} onClose={() => setQuoteOpen(false)} />
    </>
  );
}
